import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowRight, Loader2, Search, X } from 'lucide-react';
import type { ProductCard } from '@shop/shared';
import { api, qs } from '../lib/api';
import { Link, useLocale, useLocalizedNavigate, useT } from '../lib/i18n';
import { Img, Price } from './ui';
import { useTaxonomy } from './layout/Header';
import { useDebounced } from '../lib/use-debounced';

export function SearchDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim());
  const navigate = useLocalizedNavigate();
  const t = useT();
  const locale = useLocale();
  const { categories, collections } = useTaxonomy();
  const { data, isFetching } = useQuery({
    queryKey: ['search', locale, term],
    queryFn: () => api<{ products: ProductCard[]; total: number; categories: { name: string; slug: string }[] }>(`/api/store/search${qs({ q: term })}`),
    enabled: term.length >= 2,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    onOpenChange(false);
    navigate(`/search${qs({ q: q.trim() })}`);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/35 data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in" />
        <Dialog.Content className="fixed inset-x-0 top-0 z-50 max-h-[90vh] overflow-y-auto bg-bone shadow-[0_30px_60px_-30px_rgba(42,33,25,0.45)] data-[state=closed]:animate-drop-out data-[state=open]:animate-drop-in">
          <Dialog.Title className="sr-only">{t.search.title}</Dialog.Title>
          <Dialog.Description className="sr-only">{t.search.description}</Dialog.Description>
          <div className="container-page py-6 md:py-10">
            <form onSubmit={submit} className="flex items-center gap-4 border-b-[3px] border-double border-ink/70 pb-3" role="search">
              <Search className="h-5 w-5 shrink-0 text-stone" strokeWidth={1.6} />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.search.placeholder}
                className="font-display w-full bg-transparent text-2xl font-medium outline-none placeholder:text-ash md:text-4xl"
                aria-label={t.search.label}
                maxLength={100}
              />
              {isFetching && <Loader2 className="h-5 w-5 shrink-0 animate-spin text-stone" />}
              <Dialog.Close className="shrink-0 p-1" aria-label={t.search.close}>
                <X className="h-5 w-5" strokeWidth={1.5} />
              </Dialog.Close>
            </form>

            {term.length < 2 ? (
              <div className="pt-8">
                <p className="eyebrow mb-4">{t.search.popular}</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    ...categories.map((c) => ({ label: c.name, to: `/categories/${c.slug}` })),
                    ...collections.slice(0, 2).map((c) => ({ label: c.name, to: `/collections/${c.slug}` })),
                    { label: t.common.reduced, to: '/?onSale=true' },
                  ].map((s) => (
                    <Link key={s.to} to={s.to} onClick={() => onOpenChange(false)} className="typewriter rounded-[2px] border border-ink/30 bg-paper px-4 py-2 text-[14px] transition hover:border-ink">
                      {s.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : data && data.products.length === 0 ? (
              <p className="pt-10 text-stone italic">{t.search.none(term)}</p>
            ) : data ? (
              <div className="pt-8">
                {data.categories.length > 0 && (
                  <div className="mb-8 flex flex-wrap gap-2">
                    {data.categories.map((c) => (
                      <Link key={c.slug} to={`/categories/${c.slug}`} onClick={() => onOpenChange(false)} className="typewriter rounded-[2px] bg-sand px-4 py-2 text-[14px]">
                        {c.name}
                      </Link>
                    ))}
                  </div>
                )}
                <p className="eyebrow mb-5">{t.search.items}</p>
                <ul className="grid grid-cols-2 gap-x-4 gap-y-6 md:grid-cols-3 lg:grid-cols-6">
                  {data.products.map((p) => (
                    <li key={p.id}>
                      <Link to={`/products/${p.slug}`} onClick={() => onOpenChange(false)} className="group block">
                        <div className={`photo p-1! ${p.inStock ? '' : 'sold-photo'}`}>
                          <div className="aspect-[4/5] overflow-hidden bg-sand">
                            <Img src={p.image?.thumbUrl} alt={(locale === 'en' && p.image?.alt) || p.title} className="h-full w-full object-cover group-hover:scale-[1.03]" />
                          </div>
                        </div>
                        <p className="font-display mt-3 text-[1.05rem] font-medium">{p.title}</p>
                        <Price price={p.price} salePrice={p.salePrice} priceMax={p.priceMax} from={p.hasVariants} className="typewriter text-[14px]" />
                      </Link>
                    </li>
                  ))}
                </ul>
                {data.total > data.products.length && (
                  <button onClick={submit} className="typewriter mt-8 inline-flex items-center gap-2 text-[13px] font-bold tracking-[0.1em] uppercase">
                    <span className="link-underline-static">{t.search.viewAll(data.total)}</span> <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : null}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
